/**
 * Debug write-gate helpers (doc 17 §5).
 */
import {
  canRemoteWrite,
  normalizeDeliveryStatus,
  type Project,
} from "../data/mock";

export type WriteLayer = "L0" | "L1" | "L2";

export function writeGateMessage(
  project: Pick<Project, "deliveryStatus" | "auth">,
  layer: WriteLayer,
  opts?: { elevatedActive?: boolean; agentOnline?: boolean },
): string | null {
  if (layer === "L0") return null;

  if (opts?.agentOnline === false) {
    return "GATE_AGENT_OFFLINE：Agent 离线，无法下发";
  }

  const status = normalizeDeliveryStatus(project.deliveryStatus);
  const pre =
    status === "调试中" ||
    status === "未交付" ||
    status === "待激活" ||
    status === "待认领";

  if (status === "待授权") return "GATE_DELIVERY：等待业主授权";
  if (status === "已交付") return "GATE_DELIVERY：已交付，请先申请授权";

  if (layer === "L1") {
    if (pre || canRemoteWrite(project, "维护")) return null;
    return "GATE_SCOPE：需要维护或配置授权";
  }

  // L2
  if (pre) {
    if (opts?.elevatedActive === false) {
      return "GATE_ELEVATED：调试中请先开启高权限会话（可自动批准）";
    }
    return null;
  }
  if (!canRemoteWrite(project, "配置")) {
    return "GATE_SCOPE：配置写入需要「配置」授权";
  }
  if (opts && opts.elevatedActive === false) {
    return "GATE_ELEVATED：需要活跃的高权限会话";
  }
  return null;
}

export function extractGateCode(message: string | null): string | null {
  if (!message) return null;
  const idx = message.indexOf("：");
  if (idx <= 0) return null;
  return message.slice(0, idx);
}

export function denyReasonLabel(code?: string | null): string {
  switch (code) {
    case "GATE_DELIVERY":
    case "policy_denied":
      return "当前交付状态不允许写操作";
    case "GATE_SCOPE":
      return "授权范围不足";
    case "GATE_ELEVATED":
    case "elevated_required":
    case "elevated_scope_denied":
      return "需要配置范围的高权限会话";
    case "GATE_AGENT_OFFLINE":
      return "Agent 离线";
    case "agent_offline":
      return "Agent 离线";
    case "DENIED_WHITELIST":
    case "domain_not_allowed":
      return "服务不在白名单";
    case "service_not_allowed":
      return "该 homeassistant 服务未开放远程调用";
    case "SCENE_ENTITY_MISSING":
      return "场景目标实体不存在";
    case "SCENE_PROMOTE_REQUIRED":
      return "存在未转正的验证场景，请先落盘或丢弃";
    case "PACKAGE_PATH_REJECT":
      return "配置路径非法";
    case "WRITE_FAILED":
      return "写入或 reload 失败";
    case "no_revisions":
      return "没有可重新下发的托管 packages 记录";
    case "invalid_kind":
      return "不支持的配置类型";
    case "scene_not_registered":
      return "文件已写入但场景未在 HA 注册：请确认 packages 已启用；若与主机 scenes.yaml 同名会静默失败（云枢现已自动加 UDHUB · 前缀）";
    case "ha_native_readonly":
      return "该现场 HA 场景不可通过云枢删除（可能为 YAML 定义）";
    case "already_deleted":
      return "该托管场景已删除";
    case "packages_not_ready":
      return "主机未就绪 packages 加载：请在 configuration.yaml 启用 homeassistant.packages: !include_dir_named packages";
    case "path_not_allowed":
      return "配置路径不在允许范围";
    case "command_kind_not_allowed":
      return "命令类型未授权";
    case "sha256_mismatch":
      return "Agent 安装包校验失败";
    case "download_http_404":
      return "Agent 安装包不存在";
    case "command_timeout":
      return "主机执行超时";
    default:
      return code ? `失败：${code}` : "操作失败";
  }
}

/** Unified API / gate error text for toasts and disabled hints. */
export function formatWriteError(
  err: unknown,
  fallback = "操作失败",
): string {
  const code =
    (err as { code?: string })?.code ||
    (err as { error?: string })?.error ||
    extractGateCode(typeof err === "string" ? err : null);
  if (code) {
    const label = denyReasonLabel(code);
    if (label !== `失败：${code}`) return label;
  }
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "string") return err;
  return fallback;
}

export function gateDisabledTitle(
  project: Pick<Project, "deliveryStatus" | "auth">,
  layer: WriteLayer,
  opts?: { elevatedActive?: boolean; agentOnline?: boolean },
): string | undefined {
  return writeGateMessage(project, layer, opts) || undefined;
}
